import React from 'react';
import { Globe, MapPin, CheckCircle, ShieldCheck, Zap, PackageCheck, ArrowRight } from 'lucide-react';
import { SEO } from "@/components/seo/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";

const InfoListItem = ({ icon: Icon, children }) => (
    <li className="flex items-start gap-4">
        <Icon className="h-6 w-6 mt-1 text-indigo-500 flex-shrink-0" />
        <span className="text-gray-700">{children}</span>
    </li>
);

export default function UitlegNormen() {
    return (
        <>
            <SEO
                title="Uitleg Normen voor Opwekinstallaties - Sol-Vi"
                description="Een duidelijke uitleg over de Europese 'Requirements for Generators' (RfG) en de Nederlandse Netcode Elektriciteit voor zonnepanelen."
                canonical="https://sol-vi.nl/uitleg-normen"
            />
            
            <Header />
            
            <div className="min-h-screen bg-white text-gray-800 font-sans">
                <main className="container mx-auto px-6 py-20 md:py-28">
                    <section className="max-w-4xl mx-auto">
                        <div className="text-left mb-16">
                            <div className="flex flex-col lg:flex-row items-center gap-8 mb-8">
                                <div className="flex-1">
                                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
                                        Uitleg Technische Normen
                                    </h1>
                                    <p className="mt-4 text-lg text-gray-500 max-w-3xl">
                                        Wat betekenen de technische regels op de conformiteitsverklaring precies voor u? Hieronder vindt u een eenvoudige uitleg.
                                    </p>
                                </div>
                                <div className="flex-shrink-0">
                                    <img 
                                        src="/lovable-uploads/c67de092-6abf-46d9-9f5d-145f7e6d3c13.png" 
                                        alt="Elektricien werkt veilig met elektrische installaties volgens technische normen"
                                        className="w-64 h-64 object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="border-l-4 border-indigo-500 pl-6 mb-16">
                             <h2 className="text-2xl font-semibold text-gray-800">Het hoofddoel is simpel:</h2>
                             <p className="mt-2 text-gray-600 text-lg">
                                Het elektriciteitsnet in Nederland en Europa veilig, stabiel en betrouwbaar houden, nu er steeds meer zonnepanelen en batterijen worden aangesloten.
                             </p>
                        </div>

                        <div className="space-y-10">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-3">
                                        <Globe className="h-7 w-7 text-indigo-500" />
                                        1. Europese Verordening (EU) 2016/631 - "Requirements for Generators" (RfG)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        Dit is een set regels die voor de **gehele Europese Unie** geldt. Omdat het Europese elektriciteitsnet grensoverschrijdend is, is het cruciaal dat alle aangesloten installaties zich op een vergelijkbare manier gedragen. De moderne omvormer van uw installatie is de sleutel; deze is zo geprogrammeerd dat hij aan deze eisen voldoet.
                                    </p>
                                    <div className="pt-4">
                                        <h4 className="font-semibold text-gray-800 mb-4">De belangrijkste eisen zijn:</h4>
                                        <ul className="space-y-4">
                                            <InfoListItem icon={Zap}>
                                                <span><strong>Netstabiliteit ondersteunen:</strong> Uw omvormer moet kunnen omgaan met kleine schommelingen in spanning en frequentie. In plaats van direct uit te schakelen, "helpt" de installatie het net stabiel te houden.</span>
                                            </InfoListItem>
                                            <InfoListItem icon={ShieldCheck}>
                                               <span> <strong>'Fault Ride Through' (FRT):</strong> Bij een kortstondige storing elders op het net, moet uw installatie even verbonden blijven. Dit geeft het net de kans om zich te herstellen.</span>
                                            </InfoListItem>
                                            <InfoListItem icon={Zap}>
                                               <span> <strong>Actieve besturing:</strong> De omvormer kan zijn vermogen aanpassen op basis van de status van het elektriciteitsnet, om overbelasting op zonnige momenten te voorkomen.</span>
                                            </InfoListItem>
                                        </ul>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-3">
                                        <MapPin className="h-7 w-7 text-indigo-500" />
                                        2. De Nederlandse Netcode Elektriciteit
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        Dit is de **Nederlandse vertaling en uitwerking** van de Europese RfG-verordening, vastgesteld door de Autoriteit Consument & Markt (ACM). Het is in feite het Nederlandse handboek voor netbeheerders en installateurs.
                                    </p>
                                    <p className="text-gray-700 leading-relaxed">
                                       Voor u als eigenaar betekent dit dat uw installateur een omvormer moet gebruiken die niet alleen Europees is goedgekeurd, maar ook specifiek is getest en vrijgegeven voor de Nederlandse markt. De netbeheerders publiceren lijsten met goedgekeurde omvormers.
                                    </p>
                                </CardContent>
                            </Card>

                             <div className="bg-gray-50 rounded-xl p-8 mt-16">
                                <h3 className="flex items-center gap-3 text-xl font-semibold text-gray-900">
                                    <CheckCircle className="h-7 w-7 text-green-600" />
                                    Conclusie: Wat betekent dit voor u?
                                </h3>
                                <p className="text-gray-700 mt-4 leading-relaxed">
                                    U hoeft zich geen zorgen te maken over de technische details. De verantwoordelijkheid ligt bij de fabrikant en uw installateur. Door een **gecertificeerde installateur** in te schakelen die **goedgekeurde apparatuur** gebruikt, bent u verzekerd van een installatie die:
                                </p>
                                <ul className="space-y-2 mt-4 text-gray-800">
                                    <li className="flex items-center gap-3"><PackageCheck className="h-5 w-5 text-green-600" /> <strong>Veilig is:</strong> Voor u, monteurs en het net.</li>
                                    <li className="flex items-center gap-3"><PackageCheck className="h-5 w-5 text-green-600" /> <strong>Betrouwbaar is:</strong> Draagt bij aan de stabiliteit.</li>
                                    <li className="flex items-center gap-3"><PackageCheck className="h-5 w-5 text-green-600" /> <strong>Toekomstbestendig is:</strong> Klaar voor het slimme energienet.</li>
                                </ul>
                                <p className="text-gray-600 pt-4 font-medium mt-4 border-t border-gray-200">
                                    De "Verklaring van Conformiteit" is dus uw bewijs dat aan al deze complexe, maar noodzakelijke regels is voldaan.
                                </p>
                            </div>

                            <div className="mt-12 text-center">
                                <Link 
                                    to="/installatie-registreren"
                                    className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-indigo-500/50 transition-all transform hover:-translate-y-1"
                                >
                                    Terug naar registratie <ArrowRight className="h-5 w-5 ml-2" />
                                </Link>
                            </div>
                        </div>
                    </section>
                </main>
            </div>

            <Footer />
        </>
    );
}